// src/pages/PonteImobiliaria.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowRight, MapPinned, Shuffle, Building2 } from 'lucide-react';

import Listing from '../components/Listing';
import FiltroPonteImobiliaria from '../components/FiltroPonteImobiliaria';
import { useAuth } from '../context/AuthContext';
import { getAlgoliaClient } from '../services/searchClient';

import {
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { db } from '../services/firebase';

const INDEX_NAME = import.meta.env.VITE_ALGOLIA_INDEX?.trim() || 'imoveis';

const toSlug = (s = '') =>
  String(s)
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '');

const ATTRS_TO_RETRIEVE = [
  'objectID',
  'refCodigo',
  'referencia',
  'titulo',
  'descricao',
  'cidade',
  'estado',
  'bairro',
  'tipo',
  'preco',
  'dormitorios',
  'banheiros',
  'vagas',
  'thumbnail',
  'capaUrl',
  'imagens',
  'ownerId',
  'ownerNome',
  'status',
  'statusNegocio',

  // Ponte Imobiliária
  'ponteImobiliaria',
  'ponteAtiva',
  'ponteResumo',
  'motivoVenda',
  'visibilidadeRede',
  'regraComissao',
  'ponteCidadeOrigem',
  'ponteEstadoOrigem',
  'ponteCidadeDestino',
  'ponteEstadoDestino',
  'ponteCidadeOrigemSlug',
  'ponteEstadoOrigemSlug',
  'ponteCidadeDestinoSlug',
  'ponteEstadoDestinoSlug',
  'ponteOrigemSlugFull',
  'ponteDestinoSlugFull',
  'ponteTipoDesejadoDestino',
  'ponteTipoDesejadoDestinoSlug',
  'ponteCondicaoNegociacao',

  // Selos / compat
  'badges',
  'badgeKeys',
  'badgeLabels',
  'compartilharComParceiros',
  'permuta',
  'permutaResumo',
  'aceitaPermuta',
  'aceitaPermutaImoveis',
  'permutaNegociavel',
];

function mapHitToItem(hit) {
  const imgs = Array.isArray(hit?.imagens) ? hit.imagens : [];

  const thumb =
    hit?.thumbnail ||
    hit?.capaUrl ||
    (typeof imgs[0] === 'string' ? imgs[0] : imgs[0]?.url) ||
    '';

  return {
    id: hit.objectID || hit.id,
    objectID: hit.objectID || hit.id,
    thumbnail: thumb,
    imagens: imgs,
    referencia: hit.referencia || hit.refCodigo || '',
    refCodigo: hit.refCodigo || hit.referencia || '',
    origem: 'ponte',
    ...hit,
  };
}

function getPonte(item = {}) {
  return item.ponteImobiliaria || {};
}

function routeLabel(item = {}) {
  const ponte = getPonte(item);

  const origem =
    item.ponteCidadeOrigem || ponte.cidadeOrigem || item.cidade || 'Origem';

  const destino = item.ponteCidadeDestino || ponte.cidadeDestino || 'Destino';

  return `${origem} → ${destino}`;
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
          {icon}
        </div>

        <div>
          <p className="text-xs text-gray-500">{label}</p>
          <p className="text-xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
}

export default function PonteImobiliaria() {
  const { usuario } = useAuth();

  const [imoveis, setImoveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [tab, setTab] = useState('chegando');

  const [filtros, setFiltros] = useState({
    estadoOrigem: '',
    cidadeOrigem: '',
    estadoDestino: '',
    cidadeDestino: '',
    tipoDesejadoDestino: '',
    condicaoNegociacao: '',
    termo: '',
  });

  const minhaCidade = useMemo(
    () => String(usuario?.cidade || '').trim(),
    [usuario?.cidade]
  );

  const meuEstado = useMemo(
    () =>
      String(usuario?.estado || usuario?.uf || '')
        .trim()
        .toUpperCase(),
    [usuario?.estado, usuario?.uf]
  );

  useEffect(() => {
    if (!minhaCidade && !meuEstado) return;

    setFiltros((prev) => {
      if (prev.cidadeDestino || prev.estadoDestino) return prev;

      return {
        ...prev,
        cidadeDestino: minhaCidade || '',
        estadoDestino: meuEstado || '',
      };
    });
  }, [minhaCidade, meuEstado]);

  const buildAlgoliaParams = useCallback(() => {
    const facetFilters = [];
    const numericFilters = [];

    facetFilters.push(['publicado:true', 'status:publicado']);
    facetFilters.push([
      'ponteAtiva:true',
      'visibilidadeRede:ponte_imobiliaria',
    ]);

    if (tab === 'chegando') {
      if (minhaCidade) {
        facetFilters.push([`ponteCidadeDestinoSlug:${toSlug(minhaCidade)}`]);
      }

      if (meuEstado) {
        facetFilters.push([`ponteEstadoDestinoSlug:${toSlug(meuEstado)}`]);
      }
    }

    if (tab === 'saindo') {
      if (minhaCidade) {
        facetFilters.push([`ponteCidadeOrigemSlug:${toSlug(minhaCidade)}`]);
      }

      if (meuEstado) {
        facetFilters.push([`ponteEstadoOrigemSlug:${toSlug(meuEstado)}`]);
      }
    }

    if (filtros.estadoOrigem) {
      facetFilters.push([
        `ponteEstadoOrigemSlug:${toSlug(filtros.estadoOrigem)}`,
      ]);
    }

    if (filtros.cidadeOrigem) {
      facetFilters.push([
        `ponteCidadeOrigemSlug:${toSlug(filtros.cidadeOrigem)}`,
      ]);
    }

    if (filtros.estadoDestino) {
      facetFilters.push([
        `ponteEstadoDestinoSlug:${toSlug(filtros.estadoDestino)}`,
      ]);
    }

    if (filtros.cidadeDestino) {
      facetFilters.push([
        `ponteCidadeDestinoSlug:${toSlug(filtros.cidadeDestino)}`,
      ]);
    }

    if (filtros.tipoDesejadoDestino) {
      facetFilters.push([
        `ponteTipoDesejadoDestinoSlug:${toSlug(filtros.tipoDesejadoDestino)}`,
      ]);
    }

    if (filtros.condicaoNegociacao) {
      facetFilters.push([
        `ponteCondicaoNegociacao:${filtros.condicaoNegociacao}`,
      ]);
    }

    return {
      query: filtros.termo || '',
      hitsPerPage: 48,
      page: 0,
      facetFilters,
      numericFilters: numericFilters.length ? numericFilters : undefined,
      attributesToRetrieve: ATTRS_TO_RETRIEVE,
      attributesToHighlight: [],
    };
  }, [filtros, tab, minhaCidade, meuEstado]);

  const buscarFirestoreFallback = useCallback(async () => {
    const q = query(
      collection(db, 'imoveis'),
      where('visibilidadeRede', '==', 'ponte_imobiliaria'),
      orderBy('criadoEm', 'desc'),
      limit(48)
    );

    const snap = await getDocs(q);

    let items = snap.docs.map((d) => ({
      id: d.id,
      objectID: d.id,
      ...d.data(),
    }));

    if (tab === 'chegando' && minhaCidade) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(item.ponteCidadeDestino || ponte.cidadeDestino) ===
          toSlug(minhaCidade)
        );
      });
    }

    if (tab === 'saindo' && minhaCidade) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(
            item.ponteCidadeOrigem || ponte.cidadeOrigem || item.cidade
          ) === toSlug(minhaCidade)
        );
      });
    }

    if (filtros.estadoOrigem) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(item.ponteEstadoOrigem || ponte.estadoOrigem) ===
          toSlug(filtros.estadoOrigem)
        );
      });
    }

    if (filtros.cidadeOrigem) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(item.ponteCidadeOrigem || ponte.cidadeOrigem) ===
          toSlug(filtros.cidadeOrigem)
        );
      });
    }

    if (filtros.estadoDestino) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(item.ponteEstadoDestino || ponte.estadoDestino) ===
          toSlug(filtros.estadoDestino)
        );
      });
    }

    if (filtros.cidadeDestino) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(item.ponteCidadeDestino || ponte.cidadeDestino) ===
          toSlug(filtros.cidadeDestino)
        );
      });
    }

    if (filtros.tipoDesejadoDestino) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          toSlug(item.ponteTipoDesejadoDestino || ponte.tipoDesejadoDestino) ===
          toSlug(filtros.tipoDesejadoDestino)
        );
      });
    }

    if (filtros.condicaoNegociacao) {
      items = items.filter((item) => {
        const ponte = getPonte(item);
        return (
          String(item.ponteCondicaoNegociacao || ponte.condicaoNegociacao) ===
          filtros.condicaoNegociacao
        );
      });
    }

    if (filtros.termo?.trim()) {
      const termo = filtros.termo.trim().toLowerCase();

      items = items.filter((item) => {
        const texto = [
          item.titulo,
          item.refCodigo,
          item.cidade,
          item.bairro,
          item.tipo,
          item.ponteResumo,
          routeLabel(item),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        return texto.includes(termo);
      });
    }

    return items.map(mapHitToItem);
  }, [filtros, tab, minhaCidade]);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');

    try {
      const { type, index } = await getAlgoliaClient({
        indexName: INDEX_NAME,
      });

      if (type === 'algolia') {
        const params = buildAlgoliaParams();
        const res = await index.search(params.query, params);
        const items = (res.hits || []).map(mapHitToItem);
        setImoveis(items);
      } else {
        const items = await buscarFirestoreFallback();
        setImoveis(items);
      }
    } catch (err) {
      console.error('[PonteImobiliaria] erro ao carregar:', err);

      try {
        const items = await buscarFirestoreFallback();
        setImoveis(items);
      } catch (fallbackErr) {
        console.error('[PonteImobiliaria] fallback falhou:', fallbackErr);
        setErro(
          'Não foi possível carregar as oportunidades da Ponte Imobiliária.'
        );
        setImoveis([]);
      }
    } finally {
      setLoading(false);
    }
  }, [buildAlgoliaParams, buscarFirestoreFallback]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const limparFiltros = () => {
    setFiltros({
      estadoOrigem: '',
      cidadeOrigem: '',
      estadoDestino: tab === 'chegando' ? meuEstado || '' : '',
      cidadeDestino: tab === 'chegando' ? minhaCidade || '' : '',
      tipoDesejadoDestino: '',
      condicaoNegociacao: '',
      termo: '',
    });
  };

  const totalChegando = imoveis.filter((item) => {
    const ponte = getPonte(item);
    return (
      minhaCidade &&
      toSlug(item.ponteCidadeDestino || ponte.cidadeDestino) ===
        toSlug(minhaCidade)
    );
  }).length;

  const totalSaindo = imoveis.filter((item) => {
    const ponte = getPonte(item);
    return (
      minhaCidade &&
      toSlug(item.ponteCidadeOrigem || ponte.cidadeOrigem || item.cidade) ===
        toSlug(minhaCidade)
    );
  }).length;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white p-8 mb-6">
        <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" />
        <div className="absolute right-20 bottom-0 h-40 w-40 rounded-full bg-cyan-400/10 blur-2xl" />

        <div className="relative z-10 max-w-3xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 text-sm mb-4">
            <MapPinned size={16} />
            Nova ferramenta Elion
          </div>

          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
            Ponte Imobiliária
          </h1>

          <p className="mt-3 text-slate-300 text-base md:text-lg">
            Encontre proprietários que estão mudando de cidade e conecte essas
            oportunidades a corretores com imóveis no destino.
          </p>

          <div className="mt-5 flex flex-wrap gap-2 text-sm">
            <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/20">
              Migração
            </span>
            <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/20">
              Parceria automática
            </span>
            <span className="px-3 py-1 rounded-full bg-blue-500/20 border border-blue-400/20">
              Regra 50/50
            </span>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <StatCard
          icon={<Shuffle size={20} />}
          label="Oportunidades exibidas"
          value={imoveis.length}
        />

        <StatCard
          icon={<ArrowRight size={20} />}
          label="Chegando na minha cidade"
          value={totalChegando}
        />

        <StatCard
          icon={<Building2 size={20} />}
          label="Saindo da minha cidade"
          value={totalSaindo}
        />
      </div>

      {/* Tabs */}
      <div className="bg-white border border-gray-200 rounded-2xl p-2 mb-6 flex flex-col md:flex-row gap-2">
        <button
          type="button"
          onClick={() => setTab('chegando')}
          className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition ${
            tab === 'chegando'
              ? 'bg-blue-600 text-white'
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          Chegando na minha cidade
        </button>

        <button
          type="button"
          onClick={() => setTab('saindo')}
          className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition ${
            tab === 'saindo'
              ? 'bg-blue-600 text-white'
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          Saindo da minha cidade
        </button>

        <button
          type="button"
          onClick={() => setTab('todas')}
          className={`flex-1 px-4 py-3 rounded-xl text-sm font-semibold transition ${
            tab === 'todas'
              ? 'bg-blue-600 text-white'
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          Todas as oportunidades
        </button>
      </div>

      {/* Filtros separados */}
      <FiltroPonteImobiliaria
        filtros={filtros}
        setFiltros={setFiltros}
        onBuscar={carregar}
        onLimpar={limparFiltros}
        loading={loading}
      />

      {/* Resultado */}
      {erro && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-red-700 text-sm">
          {erro}
        </div>
      )}

      {loading ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-8 text-center text-gray-500">
          Carregando oportunidades da Ponte Imobiliária...
        </div>
      ) : imoveis.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center">
          <div className="mx-auto h-12 w-12 rounded-2xl bg-gray-100 text-gray-500 flex items-center justify-center mb-4">
            <MapPinned size={22} />
          </div>

          <h3 className="text-lg font-bold text-gray-900">
            Nenhuma oportunidade encontrada
          </h3>

          <p className="text-gray-500 mt-2 max-w-xl mx-auto">
            Cadastre imóveis marcados como Ponte Imobiliária ou ajuste os
            filtros para encontrar rotas de migração entre cidades.
          </p>
        </div>
      ) : (
        <Listing imoveis={imoveis} />
      )}
    </div>
  );
}
